import { useState, useCallback } from "react";
import { toast } from "sonner";
import { ZodError } from "zod";
import { fetchFastApi, ApiError } from "../api/fastapi";
import {
  StopEventRunResponseSchema,
  type StopEventRunResponse,
} from "../models/audit/stopEventRun";

// Sentinel result for a stop whose POST succeeded but whose response body
// failed schema validation: the run IS stopped server-side, so callers must
// still run their refetch path — but no per-cell details (skips, stopped
// scope) are available to drive the richer success UX.
export const STOP_RESPONSE_UNPARSED = "unparsed";

// Sentinel result for a stop whose outcome is indeterminate: the request
// died at the network level or a gateway/timeout status came back with no
// usable body. The backend performs the ENTIRE stop before responding, so a
// gateway timeout usually means the stop actually landed — callers must
// refetch so the UI catches up if it did, but must not present the stop as
// having definitely succeeded (no success toast, no highlight seeding).
export const STOP_RESPONSE_UNCONFIRMED = "unconfirmed";

export type StopEventRunResult =
  | StopEventRunResponse
  | typeof STOP_RESPONSE_UNPARSED
  | typeof STOP_RESPONSE_UNCONFIRMED
  | null;

// Statuses where the POST may have reached the backend even though no usable
// response came back (request/gateway timeouts, bad gateway, unavailable).
const INDETERMINATE_STOP_STATUSES = [408, 502, 503, 504];

const isIndeterminateStopError = (error: ApiError): boolean =>
  error.isNetworkError || INDETERMINATE_STOP_STATUSES.includes(error.status);

// Calls the stop endpoint for a single event run. Returns the parsed response
// on success, `STOP_RESPONSE_UNPARSED` when the stop landed but the response
// failed validation, `STOP_RESPONSE_UNCONFIRMED` when the transport failed in
// a way that leaves the outcome unknown (callers still refetch), or null on a
// hard failure (never throws to callers). Callers own success UX (toasts,
// highlight seeding, refetching) — this hook only reports failures, the
// unparsed-response case, and the unconfirmed case.
export function useStopEventRun() {
  const [isStopping, setIsStopping] = useState(false);

  const stopEventRun = useCallback(
    async (eventRunId: string): Promise<StopEventRunResult> => {
      setIsStopping(true);
      try {
        const response = await fetchFastApi({
          path: `/controls/v2/event-run/${eventRunId}/stop`,
          method: "POST",
        });

        return StopEventRunResponseSchema.parse(response);
      } catch (error) {
        console.error("Failed to stop event run:", error);
        if (error instanceof ZodError) {
          // The POST itself succeeded — only the response shape was
          // unexpected. Don't present the stop as failed; tell callers to
          // refetch so the UI still catches up with the cancelled run.
          toast.warning("Run stopped, but the response was unexpected");
          return STOP_RESPONSE_UNPARSED;
        }
        if (error instanceof ApiError) {
          if (isIndeterminateStopError(error)) {
            // The stop may well have landed (see the sentinel's docstring) —
            // a warning, not an error, and callers refetch to find out.
            toast.warning("Couldn't confirm the stop — refreshing");
            return STOP_RESPONSE_UNCONFIRMED;
          }
          toast.error(error.getUserMessage());
        } else {
          toast.error("Failed to stop run");
        }
        return null;
      } finally {
        setIsStopping(false);
      }
    },
    [],
  );

  return {
    stopEventRun,
    isStopping,
  };
}
